import { useState } from "react";
import { Activity, PlayCircle, Trash2 } from "lucide-react";
import api, { errMsg } from "../../lib/api";
import { toast } from "../../lib/notices";
import { DataTable, LoadingBlock, Pill, Section, StatCard, useAdminResource } from "./AdminUI";
import { formatDateTime } from "../../lib/datetime";

export default function OpsPanel() {
  const { data, loading, error, reload } = useAdminResource("/admin/ops");
  const [busy, setBusy] = useState("");

  const run = async (key, path, describe) => {
    setBusy(key);
    try {
      const r = await api.post(path);
      toast.success(describe(r.data));
      reload();
    } catch (e) { toast.error(errMsg(e, "The job could not be run")); }
    finally { setBusy(""); }
  };

  if (loading) return <LoadingBlock label="Loading operations" />;
  if (error) return <div className="brutal-card p-6 text-ink-accent text-sm" role="alert">{error}</div>;

  const q = data.queue || {};
  const statuses = Object.entries(data.projects_by_status || {}).sort((a, b) => b[1] - a[1]);

  return (
    <>
      <Section
        id="pipeline"
        title="// RENDER PIPELINE"
        actions={
          <button type="button" className="brutal-btn brutal-btn-sm brutal-btn-secondary"
                  onClick={reload} data-testid="ops-refresh">
            <Activity size={12} aria-hidden="true" /> Refresh
          </button>
        }
      >
        <div className="grid md:grid-cols-3 gap-4">
          <StatCard label="Queue" value={q.reachable ? q.queued ?? 0 : "unreachable"}
                    hint={q.reachable ? "Jobs waiting in ARQ" : q.error || "Redis not reachable"}
                    tone={q.reachable ? (q.queued > 0 ? "warn" : "good") : "bad"}
                    testid="ops-queue" />
          <StatCard label="Active renders" value={data.active_renders}
                    hint={`Worker concurrency: ${data.arq_max_jobs}`}
                    tone={data.active_renders > data.arq_max_jobs ? "warn" : "default"} />
          <StatCard label="Stale renders" value={data.stale_renders}
                    hint={data.stale_renders > 0 ? "Wedged — run the reaper" : "None wedged"}
                    tone={data.stale_renders > 0 ? "bad" : "good"} testid="ops-stale" />
          <StatCard label="Renders this month" value={data.renders.this_month}
                    hint={`Last month: ${data.renders.last_month}`} />
          <StatCard label="Storage backend" value={data.storage_backend} />
          <StatCard label="Retention" value={data.retention_days ? `${data.retention_days}d` : "off"}
                    hint={data.retention_days ? "Artifacts expire" : "Nothing is ever deleted"}
                    tone={data.retention_days ? "default" : "warn"} />
        </div>
      </Section>

      <Section id="maintenance" title="// MAINTENANCE JOBS">
        <div className="brutal-card p-6">
          <p className="text-sm text-ink-muted mb-4">
            The worker now settles orphaned renders on its own startup, so a deploy no
            longer wedges in-flight jobs. These remain for the cases it cannot see:
            the time-based sweep waits {data.stale_threshold ? "for the staleness threshold" : ""},
            and force-recover ignores it entirely.
          </p>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              className="brutal-btn"
              disabled={busy !== ""}
              onClick={() => run("reap", "/admin/ops/reap", (d) => `Reaper finished — ${d.recovered} project(s) recovered`)}
              data-testid="ops-run-reaper"
            >
              <PlayCircle size={14} aria-hidden="true" />
              {busy === "reap" ? "Running…" : "Run reaper"}
            </button>
            <button
              type="button"
              className="brutal-btn brutal-btn-secondary"
              disabled={busy !== ""}
              onClick={() => {
                if (!window.confirm(
                  "Force-recover EVERY project currently mid-render, ignoring the staleness threshold.\n\n" +
                  "Any render genuinely still running will be marked failed and refunded. " +
                  "Only do this when you know the worker is dead.")) return;
                run("force", "/admin/ops/reap?force=true",
                  (d) => `Force recovery finished — ${d.recovered} project(s) settled and refunded`);
              }}
              data-testid="ops-force-reaper"
            >
              <PlayCircle size={14} aria-hidden="true" />
              {busy === "force" ? "Running…" : "Force-recover stuck renders"}
            </button>
            <button
              type="button"
              className="brutal-btn brutal-btn-secondary"
              disabled={busy !== ""}
              onClick={() => run("retention", "/admin/ops/retention",
                (d) => d.result?.skipped ? "Retention is off (UPLOAD_RETENTION_DAYS=0)" : "Retention sweep finished")}
              data-testid="ops-run-retention"
            >
              <Trash2 size={14} aria-hidden="true" />
              {busy === "retention" ? "Running…" : "Run retention sweep"}
            </button>
          </div>
        </div>
      </Section>

      <Section id="statuses" title="// PROJECTS BY STATUS">
        <DataTable
          caption="Project counts grouped by pipeline status"
          empty="No projects."
          columns={[{ key: "status", label: "Status" }, { key: "count", label: "Count" }]}
          rows={statuses}
          renderRow={([status, count]) => (
            <tr key={status} className="border-t border-rule">
              <td className="p-3"><span className={`status-dot status-${status}`} /> {status}</td>
              <td className="p-3 tabular-nums">{count}</td>
            </tr>
          )}
        />
        <p className="text-xs text-ink-subtle mt-3">
          Stale threshold: anything active and untouched since{" "}
          <span className="font-mono-ui">{formatDateTime(data.stale_threshold)}</span>.
        </p>
      </Section>
    </>
  );
}
